import { ClipboardPasteIcon, ShieldAlertIcon } from 'lucide-react';
import { app, BridgeError, type BridgeAccount } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { InfoCard } from '@/components/task-ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD } from '@/components/ui';
import { Spinner } from '@/components/ui/spinner';

/**
 * 薯薯 · 小红书百宝箱工具页的公共部分：版本门槛、账号、提示卡、错误文案。
 * 工具页都走 `/shushu/xhs/*?accountId=`，用当前（主页上选中的）小红书账号。
 */

/** `xhs.*`、`media.save`、`app.share / resolveUrl`、账号 `uid` 从 v6 起 */
export const XHS_TOOLS_BRIDGE = 6;

export function useXhsTool() {
  const { info, loading: infoLoading, isNative } = useAppInfo();
  const { accounts, selectedId, setSelectedId, loading: accountsLoading, error, reload } = useAccounts();
  const account = accounts.find((a) => a.id === selectedId) ?? null;
  const outdated = !!info && info.bridgeVersion < XHS_TOOLS_BRIDGE;
  const loaded = !infoLoading && !accountsLoading;
  const usable = loaded && isNative && !outdated && isXhs(account) && !account?.isDemo && !!account?.canWrite;
  return { info, isNative, accounts, account, setSelectedId, outdated, loaded, usable, accountError: error, reload };
}

export function isXhs(account: BridgeAccount | null): boolean {
  return account?.platform === 'xiaohongshu';
}

/** 不能用时的说明卡（不在 App 内 / 版本旧 / 不是小红书账号 / 登录失效），能用时不渲染。 */
export function ToolNotices({
  isNative,
  loaded,
  outdated,
  account,
}: {
  isNative: boolean;
  loaded: boolean;
  outdated: boolean;
  account: BridgeAccount | null;
}) {
  if (!loaded) return null;
  const icon = <ShieldAlertIcon className="text-primary size-5 shrink-0" />;
  if (!isNative) {
    return <InfoCard title="请在 App 内打开">浏览器里单独调试请加 <code>?mock=1</code>。</InfoCard>;
  }
  if (outdated) {
    return (
      <InfoCard title="需要更新 App" icon={icon}>
        当前 App 版本还不支持这个工具，请更新后再试。
      </InfoCard>
    );
  }
  if (!isXhs(account)) {
    return (
      <InfoCard title="只支持小红书账号" icon={icon}>
        请先在主页切换到一个小红书账号。
      </InfoCard>
    );
  }
  if (account?.isDemo) {
    return (
      <InfoCard title="测试账号不能使用" icon={icon}>
        这个工具会读取真实的小红书数据，请切换到一个真实登录的小红书账号。
      </InfoCard>
    );
  }
  if (!account?.canWrite) {
    return (
      <InfoCard
        title="登录已失效"
        icon={icon}
        action={
          <Button className="w-full" onClick={() => void app.openNative('login')}>
            重新登录
          </Button>
        }
      >
        这个工具要用小红书账号的登录态读取数据，重新登录后即可使用。
      </InfoCard>
    );
  }
  return null;
}

/** 错误 → 一句人话。小红书的风控 / 限流单独说清楚该怎么办。 */
export function xhsErrorText(error: unknown): string {
  if (error instanceof BridgeError) {
    if (error.code === 'risk_verification') return '小红书要求安全验证，请先在小红书 App 里正常浏览一会儿，稍后再试。';
    if (error.code === 'rate_limited') return '请求太频繁，被小红书限流了，过几分钟再试。';
    if (error.code === 'login_required') return '小红书登录已失效，请在 App 里重新登录这个账号。';
  }
  return describeError(error);
}

export function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * 页首的大号「粘贴」按钮（对照搬小书下载 / 注册时间页）：点一下读剪贴板并直接处理，不放输入框。
 */
export function PasteButton({
  title,
  subtitle,
  busyText,
  busy,
  disabled,
  onClick,
}: {
  title: string;
  subtitle: string;
  busyText: string;
  busy: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button className="relative h-auto w-full rounded-sheet py-5 ios:h-auto" disabled={busy || disabled} onClick={onClick}>
      <span className={busy ? 'invisible flex items-center gap-3' : 'flex items-center gap-3'}>
        <ClipboardPasteIcon className="size-6.5 shrink-0" />
        <span className="text-left">
          <strong className="block text-body leading-tight font-bold">{title}</strong>
          <small className="mt-1 block text-caption1 font-normal opacity-75">{subtitle}</small>
        </span>
      </span>
      {busy ? (
        <span className="absolute inset-0 flex items-center justify-center gap-2.5 text-subheadline">
          <Spinner />
          {busyText}
        </span>
      ) : null}
    </Button>
  );
}

/** 读剪贴板：空的时候抛一句人话。 */
export async function readClipboardText(emptyHint: string): Promise<string> {
  const text = ((await app.readClipboard()).text ?? '').trim();
  if (!text) throw new Error(emptyHint);
  void app.haptic('light');
  return text;
}

/** 失败卡（对照搬小书工具页：红色图标 + 标题 + 说明）。 */
export function ErrorCard({ title, message }: { title: string; message: string }) {
  return (
    <Card className={CARD}>
      <div className="flex items-start gap-3">
        <ShieldAlertIcon className="text-destructive size-5.5 shrink-0" />
        <div>
          <strong className="block text-body font-semibold">{title}</strong>
          <p className="text-muted-foreground mt-1 text-subheadline">{message}</p>
        </div>
      </div>
    </Card>
  );
}
